import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Rocket, Target, Users, Globe2, TrendingUp, Sparkles,
    ArrowRight, ArrowLeft, Check, X, Loader2, PartyPopper,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { bl } from "@/lib/extrasApi";


const GOALS = {
    en: [
        { key: "first_campaign", icon: Rocket, label: "Launch my first cold email campaigns", hint: "You have leads, you need volume + tracking" },
        { key: "automate", icon: Sparkles, label: "Automate my marketing / outreach", hint: "You want AI to handle repetitive tasks" },
        { key: "engage", icon: Users, label: "Better engage prospects & clients", hint: "You need higher reply rates + follow-ups" },
        { key: "explore", icon: Globe2, label: "Explore before a bigger deployment", hint: "You're testing the fit for your team" },
        { key: "other", icon: Target, label: "Something else", hint: "Tell us in the last step" },
    ],
    fr: [
        { key: "first_campaign", icon: Rocket, label: "Lancer mes premières campagnes cold email", hint: "Tu as des leads, il te faut du volume + tracking" },
        { key: "automate", icon: Sparkles, label: "Automatiser mon marketing / ma prospection", hint: "Tu veux que l'IA prenne en charge le répétitif" },
        { key: "engage", icon: Users, label: "Mieux engager mes prospects & clients", hint: "Tu cherches plus de réponses + follow-ups" },
        { key: "explore", icon: Globe2, label: "Explorer avant un déploiement plus large", hint: "Tu testes si BOXEB2B convient à ton équipe" },
        { key: "other", icon: Target, label: "Autre chose", hint: "Dis-le-nous à la dernière étape" },
    ],
};

const INDUSTRIES = [
    { key: "tech", en: "Tech / SaaS", fr: "Tech / SaaS", emoji: "💻" },
    { key: "btp", en: "BTP / Construction", fr: "BTP / Bâtiment", emoji: "🏗️" },
    { key: "services", en: "B2B Services", fr: "Services B2B", emoji: "💼" },
    { key: "commerce", en: "Retail / E-commerce", fr: "Commerce / E-commerce", emoji: "🛍️" },
    { key: "tourism", en: "Tourism / Hospitality", fr: "Tourisme / Hôtellerie", emoji: "🏝️" },
    { key: "health", en: "Health / Medical", fr: "Santé / Médical", emoji: "⚕️" },
    { key: "food", en: "Food / Restaurants", fr: "Restauration", emoji: "🍽️" },
    { key: "other", en: "Other", fr: "Autre", emoji: "✨" },
];

const MARKETS = [
    { key: "france_metro", en: "France (metropolitan)", fr: "France métropolitaine", emoji: "🇫🇷" },
    { key: "dom_com", en: "DOM-COM (Guyane, Antilles...)", fr: "DOM-COM (Guyane, Antilles...)", emoji: "🌴" },
    { key: "eu", en: "European Union", fr: "Union Européenne", emoji: "🇪🇺" },
    { key: "worldwide", en: "Worldwide", fr: "International", emoji: "🌍" },
];

const TEAM_SIZES = [
    { key: "solo", en: "Just me (solo founder)", fr: "Juste moi (solo)" },
    { key: "2-5", en: "2–5 people", fr: "2–5 personnes" },
    { key: "6-20", en: "6–20 people", fr: "6–20 personnes" },
    { key: "21+", en: "21+ people", fr: "21+ personnes" },
];

const VOLUMES = [
    { key: "<50", label: "<50" },
    { key: "50-200", label: "50–200" },
    { key: "200-1000", label: "200–1000" },
    { key: "1000+", label: "1000+" },
];


export default function OnboardingWizard({ onComplete, onSkip }) {
    const { locale } = useI18n();
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState({
        goal: null,
        industry: null,
        market: null,
        team_size: null,
        monthly_leads: null,
    });
    const [saving, setSaving] = useState(false);
    const [done, setDone] = useState(false);

    const steps = ["goal", "market_industry", "team", "success"];
    const progress = ((step + 1) / steps.length) * 100;

    async function submit() {
        setSaving(true);
        try {
            const r = await api.post("/onboarding/complete", answers);
            setDone(true);
            setTimeout(() => onComplete && onComplete(r.data), 1600);
        } catch (e) {
            toast.error("Failed — you can skip and update later in Settings");
        } finally { setSaving(false); }
    }

    async function skip() {
        try { await api.post("/onboarding/skip"); } catch (_e) {}
        onSkip && onSkip();
    }

    function next() {
        if (step < steps.length - 1) setStep(step + 1);
        else submit();
    }
    function back() { if (step > 0) setStep(step - 1); }

    const canNext = (() => {
        if (step === 0) return !!answers.goal;
        if (step === 1) return !!(answers.industry && answers.market);
        if (step === 2) return !!(answers.team_size && answers.monthly_leads);
        return true;
    })();

    return (
        <div className="fixed inset-0 z-50 bg-brand-void/85 backdrop-blur-md flex items-center justify-center p-4" data-testid="onboarding-modal">
            <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className="w-full max-w-2xl rounded-3xl bg-brand-surface border border-white/10 shadow-2xl overflow-hidden"
            >
                {/* Progress bar */}
                <div className="h-1 bg-white/5">
                    <motion.div
                        className="h-full bg-gradient-to-r from-brand-indigo via-brand-violet to-brand-cyan"
                        initial={{ width: 0 }}
                        animate={{ width: `${progress}%` }}
                        transition={{ type: "spring", stiffness: 90, damping: 20 }}
                    />
                </div>

                {/* Header */}
                <div className="px-8 pt-7 pb-3 flex items-start justify-between">
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-brand-cyan mb-1">
                            {bl(`Step ${step + 1} of ${steps.length}`, `Étape ${step + 1} sur ${steps.length}`, locale)}
                        </div>
                        <h2 className="font-display font-black text-2xl tracking-tighter">
                            {step === 0 && bl("What's your #1 goal with BOXEB2B?", "Quel est ton objectif n°1 avec BOXEB2B ?", locale)}
                            {step === 1 && bl("Where are your prospects?", "Où sont tes prospects ?", locale)}
                            {step === 2 && bl("How big is your team?", "Quelle est la taille de ton équipe ?", locale)}
                            {step === 3 && bl("You're all set 🎉", "Tout est prêt 🎉", locale)}
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={skip}
                        data-testid="onboarding-skip"
                        className="text-xs text-zinc-500 hover:text-white transition inline-flex items-center gap-1"
                    >
                        <X size={12} /> {bl("Skip", "Passer", locale)}
                    </button>
                </div>

                {/* Body */}
                <div className="px-8 py-5 min-h-[300px]">
                    <AnimatePresence mode="wait">
                        {/* STEP 0 — Goal */}
                        {step === 0 && (
                            <motion.div
                                key="s0"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                className="space-y-2"
                            >
                                {GOALS[locale].map((g) => {
                                    const Icon = g.icon;
                                    const sel = answers.goal === g.key;
                                    return (
                                        <button
                                            key={g.key}
                                            type="button"
                                            onClick={() => setAnswers(a => ({ ...a, goal: g.key }))}
                                            data-testid={`onboarding-goal-${g.key}`}
                                            className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition border ${
                                                sel
                                                    ? "bg-brand-indigo/15 border-brand-indigo shadow-lg shadow-brand-indigo/10"
                                                    : "bg-brand-void/40 border-white/8 hover:border-white/25 hover:bg-brand-void/70"
                                            }`}
                                        >
                                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${sel ? "bg-brand-indigo text-white" : "bg-white/5 text-zinc-400"}`}>
                                                <Icon size={16} />
                                            </div>
                                            <div className="flex-1">
                                                <div className="text-sm font-medium text-white">{g.label}</div>
                                                <div className="text-xs text-zinc-500 mt-0.5">{g.hint}</div>
                                            </div>
                                            {sel && <Check size={14} className="text-brand-indigo shrink-0" />}
                                        </button>
                                    );
                                })}
                            </motion.div>
                        )}

                        {/* STEP 1 — Industry + Market */}
                        {step === 1 && (
                            <motion.div
                                key="s1"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                className="space-y-6"
                            >
                                <div>
                                    <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3 font-mono">
                                        {bl("Your industry", "Ton secteur", locale)}
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                        {INDUSTRIES.map((ind) => {
                                            const sel = answers.industry === ind.key;
                                            return (
                                                <button
                                                    key={ind.key}
                                                    type="button"
                                                    onClick={() => setAnswers(a => ({ ...a, industry: ind.key }))}
                                                    data-testid={`onboarding-industry-${ind.key}`}
                                                    className={`p-3 rounded-xl text-center transition border ${
                                                        sel ? "bg-brand-cyan/15 border-brand-cyan text-white" : "bg-brand-void/40 border-white/8 text-zinc-400 hover:border-white/25 hover:text-white"
                                                    }`}
                                                >
                                                    <div className="text-2xl mb-1">{ind.emoji}</div>
                                                    <div className="text-xs">{locale === "fr" ? ind.fr : ind.en}</div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div>
                                    <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3 font-mono">
                                        {bl("Your target market", "Ton marché cible", locale)}
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        {MARKETS.map((m) => {
                                            const sel = answers.market === m.key;
                                            return (
                                                <button
                                                    key={m.key}
                                                    type="button"
                                                    onClick={() => setAnswers(a => ({ ...a, market: m.key }))}
                                                    data-testid={`onboarding-market-${m.key}`}
                                                    className={`p-3 rounded-xl text-left transition border ${
                                                        sel ? "bg-brand-violet/15 border-brand-violet text-white" : "bg-brand-void/40 border-white/8 text-zinc-400 hover:border-white/25 hover:text-white"
                                                    }`}
                                                >
                                                    <div className="text-xl mb-0.5">{m.emoji}</div>
                                                    <div className="text-xs">{locale === "fr" ? m.fr : m.en}</div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {/* STEP 2 — Team + volume */}
                        {step === 2 && (
                            <motion.div
                                key="s2"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                className="space-y-6"
                            >
                                <div>
                                    <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3 font-mono">
                                        {bl("Team size", "Taille de l'équipe", locale)}
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        {TEAM_SIZES.map((t) => {
                                            const sel = answers.team_size === t.key;
                                            return (
                                                <button
                                                    key={t.key}
                                                    type="button"
                                                    onClick={() => setAnswers(a => ({ ...a, team_size: t.key }))}
                                                    data-testid={`onboarding-team-${t.key}`}
                                                    className={`p-3 rounded-xl text-sm transition border ${
                                                        sel ? "bg-brand-indigo/15 border-brand-indigo text-white" : "bg-brand-void/40 border-white/8 text-zinc-400 hover:border-white/25 hover:text-white"
                                                    }`}
                                                >
                                                    {locale === "fr" ? t.fr : t.en}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div>
                                    <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3 font-mono flex items-center gap-1.5">
                                        <TrendingUp size={11} /> {bl("Leads you want to reach per month", "Leads que tu veux contacter/mois", locale)}
                                    </div>
                                    <div className="grid grid-cols-4 gap-2">
                                        {VOLUMES.map((v) => {
                                            const sel = answers.monthly_leads === v.key;
                                            return (
                                                <button
                                                    key={v.key}
                                                    type="button"
                                                    onClick={() => setAnswers(a => ({ ...a, monthly_leads: v.key }))}
                                                    data-testid={`onboarding-volume-${v.key}`}
                                                    className={`p-3 rounded-xl text-center font-mono text-sm transition border ${
                                                        sel ? "bg-brand-cyan/15 border-brand-cyan text-white" : "bg-brand-void/40 border-white/8 text-zinc-400 hover:border-white/25 hover:text-white"
                                                    }`}
                                                >
                                                    {v.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {/* STEP 3 — Success */}
                        {step === 3 && (
                            <motion.div
                                key="s3"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.95 }}
                                className="flex flex-col items-center justify-center text-center py-6"
                            >
                                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-indigo to-brand-cyan flex items-center justify-center mb-4">
                                    <PartyPopper size={26} className="text-white" />
                                </div>
                                <p className="text-zinc-300 max-w-md">
                                    {bl(
                                        "We'll pre-configure your workspace based on your answers. Your first segment (\"🎯 My ICP\") is being created and 50 free credits are already loaded.",
                                        "On pré-configure ton workspace avec tes réponses. Ton premier segment (« 🎯 My ICP ») est en cours de création et tes 50 crédits offerts sont déjà chargés.",
                                        locale,
                                    )}
                                </p>
                                <div className="mt-6 grid grid-cols-2 gap-3 text-xs text-zinc-400 w-full max-w-sm">
                                    <div className="rounded-lg bg-brand-void/40 border border-white/8 p-3">
                                        <div className="text-brand-cyan font-mono text-lg">50</div>
                                        <div>{bl("free credits", "crédits offerts", locale)}</div>
                                    </div>
                                    <div className="rounded-lg bg-brand-void/40 border border-white/8 p-3">
                                        <div className="text-brand-cyan font-mono text-lg">1</div>
                                        <div>{bl("ICP segment created", "segment ICP créé", locale)}</div>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Footer */}
                <div className="px-8 pb-7 pt-3 flex items-center justify-between border-t border-white/6">
                    <button
                        type="button"
                        onClick={back}
                        disabled={step === 0 || saving}
                        data-testid="onboarding-back"
                        className="text-sm text-zinc-400 hover:text-white disabled:opacity-30 inline-flex items-center gap-1"
                    >
                        <ArrowLeft size={13} /> {bl("Back", "Retour", locale)}
                    </button>

                    <button
                        type="button"
                        onClick={next}
                        disabled={!canNext || saving || done}
                        data-testid="onboarding-next"
                        className="btn-cta text-white text-sm font-semibold px-5 py-2.5 rounded-full inline-flex items-center gap-2 disabled:opacity-50"
                    >
                        {saving || done ? <Loader2 size={13} className="animate-spin" /> : null}
                        {step === steps.length - 1
                            ? bl("Go to my dashboard", "Aller au dashboard", locale)
                            : bl("Continue", "Continuer", locale)}
                        {!saving && !done && <ArrowRight size={13} />}
                    </button>
                </div>
            </motion.div>
        </div>
    );
}
